import React from 'react';
import { Bar } from 'react-chartjs-2';
import KpiChartPanel from '../kpi-dashboard/charts/KpiChartPanel';
import { barColorsForIndex, type TimeByGroupBarRow } from './bandwidthMockData';
import { formatAxisCompactK } from '../kpi-dashboard/chartFormatters';
import { fmtBwDecimal } from './bandwidthFormat';

export interface BandwidthTimeByGroupBarProps {
  title: string;
  subtitle?: string;
  rows: TimeByGroupBarRow[];
  /** When rows are empty, show this instead of the default no-data message (e.g. loading / error). */
  emptyHint?: string;
  /** Click a bar to cross-filter other charts (toggle same bar to clear). */
  interactive?: boolean;
  selectedLabel?: string | null;
  onBarClick?: (row: TimeByGroupBarRow, index: number) => void;
}

const BandwidthTimeByGroupBar: React.FC<BandwidthTimeByGroupBarProps> = ({
  title,
  subtitle,
  rows,
  emptyHint,
  interactive = false,
  selectedLabel = null,
  onBarClick,
}) => {
  const labels = rows.map((r) => r.label);
  const data = rows.map((r) => r.hours);
  const bg = rows.map((r, i) =>
    selectedLabel != null && r.label === selectedLabel ? 'rgba(68, 114, 196, 0.95)' : barColorsForIndex(i)
  );

  if (rows.length === 0) {
    return (
      <KpiChartPanel
        title={title}
        titleClassName="!pl-0"
        className="h-full min-h-0 max-h-[400px]"
        flexFill
        bodyClassName="px-3 pb-4 pt-4"
      >
        {subtitle ? (
          <p className="mb-2 text-xs leading-snug text-slate-500">{subtitle}</p>
        ) : null}
        <p className="py-12 text-center text-sm text-slate-500">
          {emptyHint ?? 'No data for the selected filters.'}
        </p>
      </KpiChartPanel>
    );
  }

  return (
    <KpiChartPanel
      title={title}
      titleClassName="!pl-0"
      className="h-full min-h-0 max-h-[400px]"
      flexFill
      bodyClassName="px-3 pb-4 pt-4"
    >
      {subtitle ? (
        <p className="mb-2 text-xs leading-snug text-slate-500">{subtitle}</p>
      ) : null}
      <div className="relative h-[280px] w-full min-h-0 flex-1">
        <Bar
          data={{
            labels,
            datasets: [
              {
                label: 'Hours',
                data,
                backgroundColor: bg,
                borderWidth: 0,
                borderRadius: 4,
                barThickness: 18,
                datalabels: {
                  display: true,
                  anchor: 'end',
                  align: 'end',
                  offset: 4,
                  clip: false,
                  color: '#0f172a',
                  font: { size: 10, weight: 600 },
                  formatter: (value: number) =>
                    typeof value === 'number' ? fmtBwDecimal(value) : '',
                },
              },
            ],
          }}
          options={{
            indexAxis: 'y',
            responsive: true,
            maintainAspectRatio: false,
            layout: { padding: { right: 48, top: 8, bottom: 8, left: 4 } },
            onClick: (_event, elements) => {
              if (!interactive || !onBarClick || elements.length === 0) return;
              const idx = elements[0].index;
              if (typeof idx !== 'number') return;
              const row = rows[idx];
              if (row) onBarClick(row, idx);
            },
            onHover: (event, elements) => {
              const el = event.native?.target;
              if (el instanceof HTMLElement) {
                el.style.cursor = interactive && elements.length > 0 ? 'pointer' : 'default';
              }
            },
            plugins: {
              legend: { display: false },
              tooltip: {
                callbacks: {
                  label: (ctx) => {
                    const v = ctx.parsed.x;
                    return typeof v === 'number'
                      ? `${fmtBwDecimal(v)} hrs`
                      : '';
                  },
                },
              },
            },
            scales: {
              x: {
                beginAtZero: true,
                grid: { color: 'rgba(148, 163, 184, 0.25)' },
                ticks: {
                  maxTicksLimit: 8,
                  callback: (value) => formatAxisCompactK(Number(value)),
                },
                title: { display: true, text: 'Hours', color: '#64748b', font: { size: 11 } },
              },
              y: {
                grid: { display: false },
                ticks: { font: { size: 10 } },
              },
            },
          }}
        />
      </div>
    </KpiChartPanel>
  );
};

export default BandwidthTimeByGroupBar;
